import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { fileToCompressedDataUrl } from '../../utils/image';

const MAX_IMAGENES = 3;

const ESTADOS = {
  en_proceso: { label: 'En proceso', color: 'info' },
  atendido:   { label: 'Atendido',   color: 'warning' },
  cerrado:    { label: 'Cerrado',    color: 'success' },
};

const fmtFecha = (f) => {
  if (!f) return '';
  const d = new Date(String(f).replace(' ', 'T'));
  return isNaN(d) ? f : d.toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });
};

export default function SupportPage() {
  const [tickets, setTickets]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [descripcion, setDescripcion] = useState('');
  const [imagenes, setImagenes]       = useState([]); // data-URLs listos para enviar
  const [sending, setSending]         = useState(false);
  const [success, setSuccess]         = useState('');
  const [error, setError]             = useState('');
  const [fotosDe, setFotosDe]         = useState({}); // { [ticketId]: [dataUrl,...] }
  const [ampliada, setAmpliada]       = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/tickets/mine');
      setTickets(res.data);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  };

  const notify = (msg, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(''), 4000); }
    else          { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); }
  };

  const agregarImagenes = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!files.length) return;
    if (imagenes.length + files.length > MAX_IMAGENES) {
      return notify(`Máximo ${MAX_IMAGENES} imágenes por ticket`, true);
    }
    try {
      const nuevas = await Promise.all(files.map(f => fileToCompressedDataUrl(f)));
      setImagenes(prev => [...prev, ...nuevas]);
    } catch (err) {
      notify(err.message || 'No se pudo procesar la imagen', true);
    }
  };

  const quitarImagen = (idx) => setImagenes(prev => prev.filter((_, i) => i !== idx));

  const enviar = async () => {
    if (!descripcion.trim()) return notify('Escribe la descripción de la novedad', true);
    setSending(true);
    try {
      await api.post('/tickets', { descripcion, imagenes });
      setDescripcion('');
      setImagenes([]);
      notify('Ticket enviado. Soporte lo revisará pronto.');
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'Error enviando el ticket', true);
    } finally { setSending(false); }
  };

  const verFotos = async (ticketId) => {
    if (fotosDe[ticketId]) {
      setFotosDe(prev => { const { [ticketId]: _omitida, ...resto } = prev; return resto; });
      return;
    }
    try {
      const res = await api.get(`/tickets/${ticketId}/images`);
      setFotosDe(prev => ({ ...prev, [ticketId]: res.data.imagenes || [] }));
    } catch { notify('No se pudieron cargar las imágenes', true); }
  };

  const thumbStyle = {
    width: 84, height: 84, objectFit: 'cover', borderRadius: 8,
    border: '1px solid var(--color-border)', cursor: 'pointer',
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Soporte</h1>
          <p className="page-subtitle">Reporta una novedad al equipo de soporte</p>
        </div>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>}
      {error   && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* Nuevo ticket */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ padding: '1.5rem' }}>
          <div className="form-group">
            <label className="form-label">Descripción de la novedad</label>
            <textarea
              className="form-input"
              rows={4}
              value={descripcion}
              onChange={e => setDescripcion(e.target.value)}
              placeholder="Describe el problema o la novedad que quieres reportar..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Imágenes (opcional, máx. {MAX_IMAGENES})</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {imagenes.map((img, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={img} alt={`Adjunto ${i + 1}`} style={thumbStyle} onClick={() => setAmpliada(img)} />
                  <button
                    onClick={() => quitarImagen(i)}
                    title="Quitar imagen"
                    style={{
                      position: 'absolute', top: -6, right: -6, width: 22, height: 22,
                      borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff',
                      cursor: 'pointer', fontSize: '0.7rem', lineHeight: 1,
                    }}
                  >✕</button>
                </div>
              ))}
              {imagenes.length < MAX_IMAGENES && (
                <label style={{
                  width: 84, height: 84, borderRadius: 8, border: '2px dashed var(--color-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                  cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '0.7rem', gap: 2,
                }}>
                  <span style={{ fontSize: '1.3rem' }}>📷</span>
                  Adjuntar
                  <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={agregarImagenes} />
                </label>
              )}
            </div>
          </div>

          <button className="btn btn-primary" onClick={enviar} disabled={sending}>
            {sending ? 'Enviando...' : '📨 Enviar ticket'}
          </button>
        </div>
      </div>

      {/* Historial */}
      <div className="card">
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Mis tickets</h3>
        </div>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
        ) : tickets.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Aún no has enviado tickets
          </div>
        ) : (
          tickets.map(t => {
            const est = ESTADOS[t.estado] || { label: t.estado, color: 'secondary' };
            return (
              <div key={t.id} style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--color-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    {fmtFecha(t.creado_en)} · {t.user_name}
                  </span>
                  <span className={`badge badge-${est.color}`} style={{ fontSize: '0.65rem' }}>{est.label}</span>
                </div>
                <div style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>{t.descripcion}</div>
                {!!t.tiene_imagenes && (
                  <button className="btn btn-ghost" style={{ fontSize: '0.75rem', marginTop: 6, padding: '2px 8px' }} onClick={() => verFotos(t.id)}>
                    📷 {fotosDe[t.id] ? 'Ocultar imágenes' : 'Ver imágenes'}
                  </button>
                )}
                {fotosDe[t.id] && (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                    {fotosDe[t.id].map((img, i) => (
                      <img key={i} src={img} alt={`Adjunto ${i + 1}`} style={thumbStyle} onClick={() => setAmpliada(img)} />
                    ))}
                  </div>
                )}
                {t.detalle_solucion && (
                  <div style={{
                    marginTop: 8, padding: '0.75rem', borderRadius: 8,
                    background: 'var(--color-surface)', fontSize: '0.85rem', lineHeight: 1.5,
                  }}>
                    <strong style={{ color: 'var(--color-gold)' }}>Solución:</strong> {t.detalle_solucion}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Visor de imagen ampliada */}
      {ampliada && (
        <div
          onClick={() => setAmpliada(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: '2rem',
          }}
        >
          <img src={ampliada} alt="Imagen ampliada" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}
